<template>
  <v-container fluid class="px-8">
    <v-card class="mb-2" outlined v-if="Object.keys(form.applicant).length">
      <!-- Applicant Details -->
      <v-card-title
        class="primary white--text text-subtitle-2 pa-2 pl-6 font-weight-bold"
      >
        Applicant Details
      </v-card-title>
      <v-card-text class="mt-2 px-4">
        <div class="custom-grid">
          <!-- Profile Photo -->
          <div class="grid-item div1">
            <ProfileUpload
              v-model="form.applicant.photoImage"
              :photo="form.applicant.photo"
              :isReadOnly="true"
            />
          </div>

          <!-- DEB ID -->
          <div class="grid-item div3">
            <div class="field-container secondary">
              <span class="field-label">DEB ID</span>
              <span class="field-value">{{ form.applicant.deb_id }}</span>
            </div>
          </div>

          <!-- ABC ID -->
          <div class="grid-item div4">
            <div class="field-container secondary">
              <span class="field-label">ABC ID</span>
              <span class="field-value">{{
                form.applicant.abc_id || "-"
              }}</span>
            </div>
          </div>

          <div class="grid-item div6">
            <div class="field-container secondary">
              <span class="field-label"
                >Name(As per the latest certificate)</span
              >
              <span class="field-value">{{ form.applicant.name }}</span>
            </div>
          </div>

          <div class="grid-item div6">
            <div class="field-container secondary">
              <span class="field-label">Name(As per DEB)</span>
              <span class="field-value">{{
                form.applicant?.deb_map?.name || "-"
              }}</span>
            </div>
          </div>

          <!-- DOB -->
          <div class="grid-item div5">
            <div class="field-container secondary">
              <span class="field-label">Date of Birth</span>
              <span class="field-value">{{ form.applicant.dob }}</span>
            </div>
          </div>

          <!-- Programme -->
          <div class="grid-item div6">
            <div class="field-container secondary">
              <span class="field-label">Programme</span>
              <span class="field-value">{{
                getProgramName(form.applicant.program_id)
              }}</span>
            </div>
          </div>

          <!-- Dynamic fields -->
          <template
            v-for="(value, key, index) in Object.entries(form.applicant)"
          >
            <template v-if="isObject(value[1], value[0])">
              <div
                class="grid-item section-title primary--text"
                :key="`title-${key}`"
              >
                {{ replaceName(value[0]) }}
              </div>
              <div class="grid-item div6" :key="`div6-${key}`">
                <div class="field-container secondary">
                  <span class="field-label">Name</span>
                  <span class="field-value">{{
                    form.applicant.family.name
                  }}</span>
                </div>
              </div>
              <template v-for="(subValue, subKey) in value[1]">
                <div
                  class="grid-item field-item"
                  v-if="getLabel(subKey)"
                  :key="`${subKey}-${index}`"
                >
                  <div class="field-container secondary">
                    <span class="field-label">{{ getLabel(subKey) }}</span>
                    <span
                      class="field-value"
                      v-if="
                        subKey === 'relationship' &&
                        form.applicant.family.relationship === 'Other'
                      "
                    >
                      {{ form.applicant.family.relation_type }}
                    </span>
                    <span
                      class="field-value"
                      v-else-if="subKey === 'mobile_no'"
                    >
                      {{ form.applicant.family.mobile_prefix }}
                      {{ form.applicant.family.mobile_no }}
                    </span>
                    <span class="field-value" v-else>
                      {{ formatValue(subKey, subValue) }}
                    </span>
                  </div>
                </div>
              </template>
            </template>
            <div
              class="grid-item field-item"
              v-else-if="getLabel(value[0])"
              :key="value[0]"
            >
              <div class="field-container secondary">
                <span class="field-label">{{ getLabel(value[0]) }}</span>
                <span class="field-value">{{
                  formatValue(value[0], value[1])
                }}</span>
              </div>
            </div>
          </template>
        </div>
      </v-card-text>
    </v-card>
    <!-- Address Details -->
    <v-card class="mb-2" outlined v-if="Object.keys(form.address).length">
      <v-card-title
        class="primary white--text text-subtitle-2 pa-2 pl-6 font-weight-bold"
      >
        Address Details</v-card-title
      >
      <v-card-text class="mt-2 px-4">
        <v-row dense>
          <template v-for="addressType in getAddressSections()">
            <v-col
              cols="12"
              :key="addressType.key + '-title'"
              v-if="getAddressSections().length > 1"
            >
              <div class="section-title primary--text">
                {{ replaceName(addressType.label) }}
              </div>
            </v-col>
            <v-col cols="12" :key="addressType.key + '-content'">
              <div class="field-container secondary">
                <span class="field-label">{{ getLabel(addressType.key) }}</span>
                <span class="field-value">
                  {{
                    Object.entries(addressType.data)
                      .filter(([k, _]) => getLabel(k))
                      .map(([k, v]) =>
                        formatValue(k, v) !== "-" ? formatValue(k, v) : "",
                      )
                      .filter(Boolean)
                      .join(", ")
                  }}
                </span>
              </div>
            </v-col>
          </template>
        </v-row>
      </v-card-text>
    </v-card>
    <!-- Education Details -->
    <v-card class="mb-2" outlined v-if="form.education.length">
      <v-card-title
        class="primary white--text text-subtitle-2 pa-2 pl-6 font-weight-bold"
      >
        Education Details
      </v-card-title>
      <v-card-text class="mt-2 px-4">
        <v-row dense>
          <template v-for="(education, index) in form.education">
            <v-col cols="12" sm="4" :key="'edu-inline-' + index">
              <div
                class="field-container secondary"
                v-if="education.qualification_name != 'Kovida'"
              >
                <div class="field-label">
                  {{ education.qualification_name.toUpperCase() }}
                </div>
                <div class="field-value">
                  {{ education.name_of_institution }}
                </div>
              </div>
            </v-col>
          </template>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row v-if="!isReadOnly" class="pl-2">
      <div class="d-flex justify-start">
        <v-checkbox
          v-model="form.applicant.agree"
          :rules="[rules.required]"
          hide-details
          class="mt-4"
        ></v-checkbox>
        <label class="mt-5">
          I hereby declare that all the information provided by me in this
          application form is true and correct to the best of my
          knowledge.</label
        >
      </div>
    </v-row>
    <!-- Action Buttons -->
    <v-row v-if="!isReadOnly" class="form-row no-print mt-10">
      <v-col cols="12" class="d-flex justify-end pr-0">
        <v-btn
          :disabled="!form.applicant.agree"
          class="mr-3"
          color="success"
          :loading="loading || submitLoading"
          @click="handleSubmit"
        >
          Submit
        </v-btn>
      </v-col>
    </v-row>
    <v-dialog v-model="openPartnerDeclarationDialog" max-width="650" persistent>
      <v-card>
        <v-form ref="form">
          <v-card-title class="pl-4 py-1 white--text primary">
            Declaration
            <v-spacer></v-spacer>
            <v-btn small icon @click="openPartnerDeclarationDialog = false">
              <v-icon small color="secondary">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text
            class="body-2 text-justify pt-2"
            style="line-height: 1.6"
          >
            {{ declaration.content }}
            <v-divider class=""></v-divider>
            <v-checkbox
              v-model="sp_acceptance"
              :label="declaration.acceptance_term"
              hide-details
              class="pa-0"
            ></v-checkbox>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              text
              color="error"
              @click="openPartnerDeclarationDialog = false"
            >
              Cancel
            </v-btn>
            <v-btn
              text
              color="success"
              :loading="submitLoading"
              :disabled="!sp_acceptance"
              @click="$emit('submit', sp_acceptance)"
            >
              Yes
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <!-- Failure Payment Dialog -->
    <v-dialog v-model="showFailureDialog" max-width="500" persistent>
      <v-card>
        <v-card-title
          class="pl-4 py-2 white--text error"
          style="background-color: #dc3545"
        >
          Payment Failed
          <v-spacer></v-spacer>
          <v-btn small icon @click="showFailureDialog = false">
            <v-icon small color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <div class="text-center mb-4">
            <v-icon size="48" color="error">mdi-alert-circle</v-icon>
          </div>
          <p class="text-body-1 text-center mb-0">
            {{ failureDialogMessage }}
          </p>
          <p class="text-caption text-center grey--text mt-2">
            Your payment was unsuccessful. You can still submit your application
            and pay later for add-on programmes.
          </p>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            color="grey darken-1"
            @click="handleFailureDialogAction('cancel')"
          >
            Cancel
          </v-btn>
          <v-btn
            text
            color="primary"
            @click="handleFailureDialogAction('view-addons')"
          >
            View Add-on Programme
          </v-btn>
          <v-btn
            text
            color="success"
            @click="handleFailureDialogAction('submit')"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showPaymentDialog" max-width="520px" persistent>
      <v-card class="px-4 rounded-xl elevation-12" ref="downloadContent">
        <v-card-title
          class="text-body-1 text-3xl font-weight-bold text-primary"
        >
          Payment Summary
          <v-spacer></v-spacer>
          <v-btn icon large @click="showPaymentDialog = false">
            <v-icon small color="red">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pb-3">
          <v-simple-table dense class="text-caption pb-3">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Particular</td>
                  <td>Add-on Programme</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Bank Ref. No</td>
                  <td>{{ payment_order_status.bank_ref_no }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Bank ID</td>
                  <td>{{ payment_order_status.bankid }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Charge Amount</td>
                  <td>{{ payment_order_status.charge_amount }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Mode</td>
                  <td>
                    {{
                      payment_order_status.payment_method_type
                        .charAt(0)
                        .toUpperCase() +
                      payment_order_status.payment_method_type.slice(1)
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Payment Status</td>
                  <td
                    :class="{
                      'error--text':
                        payment_order_status.auth_status === '0399' ||
                        payment_order_status.auth_status === 'NOT RECEIVED',
                      'warning--text':
                        payment_order_status.auth_status === '0002',
                      'success--text':
                        payment_order_status.auth_status !== '0399' &&
                        payment_order_status.auth_status !== '0002' &&
                        payment_order_status.auth_status !== 'NOT RECEIVED',
                    }"
                  >
                    {{
                      payment_order_status.auth_status === "0300"
                        ? "Success"
                        : payment_order_status.auth_status === "0399"
                        ? "Failed"
                        : payment_order_status.auth_status === "0002"
                        ? "Pending"
                        : payment_order_status.auth_status === "NOT RECEIVED"
                        ? "Amount not received"
                        : payment_order_status.auth_status
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Transaction Status</td>
                  <td>{{ payment_order_status.transaction_error_desc }}</td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Transaction ID</td>
                  <td>
                    {{ payment_order_status.transactionid }}
                    <v-btn
                      icon
                      small
                      @click="
                        copyToClipboard(payment_order_status.transactionid)
                      "
                    >
                      <v-icon small class="ml-1">mdi-content-copy</v-icon>
                    </v-btn>
                  </td>
                </tr>
                <tr>
                  <td class="font-weight-bold pr-4 pl-0">Transaction Date</td>
                  <td>
                    {{ payment_order_status.transaction_date }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!--<span
            v-if="payment_status === '0300'"
            class="py-3 text-caption break-all text-caption font-weight-bold"
          >
            Note: Payment details sent via email. Please proceed to the next
            step to complete your application.
          </span>-->
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="confirmationDialog" width="450" persistent>
      <confirmationDialogForm
        v-if="confirmationDialog"
        :confirmationText="confirmationText"
        @closeDialog="confirmationDialog = false"
        @saveDialog="proceedWithSubmission()"
      />
    </v-dialog>
    <!-- Add this after your existing dialog -->
    <ProgramAddonsDialog
      v-model="openProgramAddonsDialog"
      :applicant-id="form.applicant?.id"
      :program-id="form.applicant?.program_id || null"
      :partner-id="form.applicant?.partner_id || null"
      :batch="form.applicant?.batch || null"
      :year="form.applicant?.year || null"
      :existing-program-addons="
        applicant_addon_programmes?.program_addons || []
      "
      :existing-program-addon-amount="applicant_addon_programmes?.amount"
      :show-payments="false"
      @submit="handleAddonsSubmit"
      @skipAddons="handleSkipAddons"
    />
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { cloneDeep } from "lodash";
import ProfileUpload from "./ProfileUpload.vue";
import ProgramAddonsDialog from "@/components/ProgramAddonsDialog.vue";
import confirmationDialogForm from "../components/confirmationDialog.vue";
import env from "@/helpers/env";

export default {
  components: { ProfileUpload, ProgramAddonsDialog, confirmationDialogForm },

  data() {
    return {
      rules,
      loading: false,
      env: env,
      fieldLabels: {
        gender: "Gender",
        mobile_no: "Mobile Number",
        whatsapp_mobile_no: "WhatsApp Number",
        email: "Personal Email",
        alternate_email: "Alternate Email",
        country_id: "Nationality",
        religion_id: "Religion",
        caste_id: "Caste",
        degree_id: "Degree",
        community_id: "Reservation Category",
        residence: "Resident Status",
        indian_air_force: "Are you serving in Air Force",
        relationship: "Relation",
        name_of_institution: "Name of Institution",
        // board: "Board",
        // location: "Location",
        // result_type: "Result Type",
        // marks_obtained: "Marks Obtained",
        // grade: "Grade",
        // total_marks: "Total Marks",
        // is_indian_address: "Is Indian Address",
        address_line1: "Address Line 1",
        address_line2: "Address Line 2",
        city: "City",
        state: "State",
        country: "Country",
        postcode: "Postcode",
        // locality: "Locality",
        pan_card_no: "PAN Number",
        aadhar_card_no: "Aadhaar Number",
        passport_number: "Passport Number",
        passport_date_of_issue: "Passport Date of Issue",
        passport_place_of_issue: "Passport Place of Issue",
      },
      countrys: [],
      religions: [],
      castes: [],
      communitys: [],
      degrees: [],
      programs: [],
      form: {
        applicant: {},
        address: {},
        education: [],
      },
      openPartnerDeclarationDialog: false,
      declaration: {},
      sp_acceptance: false,
      openProgramAddonsDialog: false,
      showPaymentDialog: false,
      payment_status: null,
      currentSelectedOption: null,
      applicant_addon_programmes: [],
      currentSelectedAddon: null,
      selectedInvoice: {},
      confirmationDialog: false,
      confirmationText: "",
      payment_order_status: {},
      showFailureDialog: false,
      failureDialogMessage: "Are you sure you want to submit your application?",
      addonfeeSchedules: [],
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
    submitLoading: { type: Boolean, default: false },
  },
  watch: {
    value: {
      handler(newVal) {
        this.form = cloneDeep(newVal);
      },
      deep: true,
    },
  },
  async created() {
    this.form = cloneDeep(this.value);
    if (this.form.applicant.application_status === "Pending") {
      this.form.applicant.agree = false;
    }
    const { invoice_id, status, order_id } = this.$route.query;

    if (order_id) {
      await this.getPaymentOrder(order_id);
      this.showPaymentDialog = true;
    }
    this.payment_status = status;
    if (status === "0300") {
      this.$store.commit("showSnackbar", {
        message: "Payment successfully completed",
        color: "success",
        timeout: 5000,
      });
      this.showPaymentDialog = true;
    }
    this.init();
  },
  methods: {
    async getPaymentOrder(orderid) {
      await this.$axios.get("/get_payment_order/" + orderid).then((res) => {
        this.payment_order_status = res.data.data;
        this.payment_status = this.payment_order_status.auth_status;
      });
    },
    validEducationDetails(data) {
      return true;
      // data.name_of_institution
      // &&
      //   data.board &&
      //   data.location &&
      //   data.result_type &&
      //   data.marks_obtained) ||
      //   data.grade(data.result_type !== "marks" || data.total_marks
    },
    shouldDisplayField(subKey, education) {
      if (
        (education.result_type !== "grade" && subKey === "grade") ||
        (education.result_type === "grade" && subKey === "marks_obtained") ||
        (education.result_type !== "marks" && subKey === "total_marks")
      ) {
        return false;
      }
      return true;
    },
    async init() {
      this.loading = true;
      await Promise.all([
        this.getReligions(),
        this.getCastes(),
        this.getCommunities(),
        this.getCountries(),
        this.getDegrees(),
        this.getPrograms(),
        this.getApplicantAddonProgramme(),
        this.fetchFeeSchedules(),
      ]);
      this.loading = false;
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get("/programs");
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },
    async getDegrees() {
      try {
        const response = await this.$axios.get("/degrees");
        this.degrees = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getReligions() {
      try {
        const response = await this.$axios.get("/religions");
        this.religions = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCastes() {
      try {
        const response = await this.$axios.get("/castes");
        this.castes = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCommunities() {
      try {
        const response = await this.$axios.get("/communities");
        this.communitys = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCountries() {
      try {
        const response = await this.$axios.get("/countries");
        this.countrys = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    handleFailureDialogAction(action) {
      this.showFailureDialog = false;

      switch (action) {
        case "cancel":
          this.$store.commit("showSnackbar", {
            message: "Submission cancelled",
            color: "info",
            timeout: 3000,
          });
          break;

        case "view-addons":
          this.openProgramAddonsDialog = true;
          break;

        case "submit":
          this.proceedWithSubmission();
          break;
      }
    },
    async handleSubmit() {
      const isAddonPaid =
        this.applicant_addon_programmes?.is_amount_paid === true;
      if (isAddonPaid) {
        this.openProgramAddonsDialog = false;
        this.proceedWithSubmission();
        return;
      }
      if (this.payment_order_status.auth_status === "0300") {
        this.openProgramAddonsDialog = false;
        this.proceedWithSubmission();
        return;
      }

      if (this.payment_order_status.auth_status === "0399") {
        this.showFailureDialog = true;
        return;
      }

      const hasNoAddons =
        !this.applicant_addon_programmes?.program_addons ||
        this.applicant_addon_programmes?.program_addons?.length === 0;

      const hasNotSkippedOrSelected = this.currentSelectedOption !== "now";

      if (
        hasNoAddons &&
        hasNotSkippedOrSelected &&
        this.addonfeeSchedules.length > 0
      ) {
        this.openProgramAddonsDialog = true;
        return;
      }

      this.proceedWithSubmission();
    },
    async proceedWithSubmission() {
      this.loading = true;
      try {
        const response = await this.$axios.get(
          `/get_declaration_configuration/${this.form.applicant.partner_code}`,
        );
        const declaration = response?.data?.data;

        if (!declaration) {
          this.$emit("submit");
        } else if (this.form.applicant?.sp_acceptance) {
          this.$emit("submit", this.form.applicant.sp_acceptance);
        } else {
          this.openPartnerDeclarationDialog = true;
          this.declaration = declaration;
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    getLabel(key) {
      const residenceValue = Object.entries(this.form.applicant).find(
        ([key, value]) => key === "residence",
      )[1];
      if (residenceValue == "true") {
        if (
          this.fieldLabels[key] == "Passport Number" ||
          this.fieldLabels[key] == "Passport Date of Issue" ||
          this.fieldLabels[key] == "Passport Place of Issue"
        ) {
          return;
        }
      } else if (residenceValue == "false") {
        if (
          this.fieldLabels[key] == "Aadhaar Number" ||
          this.fieldLabels[key] == "PAN Number"
        ) {
          return;
        }
      }
      return this.fieldLabels[key];
    },
    replaceName(key) {
      return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatValue(key, value) {
      if (value === null || value === "") return "-";
      if (key === "residence") {
        return value === "true" ? "Resident Indian" : "Non Resident Indian";
      }
      if (typeof value === "boolean") return value ? "Yes" : "No";

      if (typeof value === "string") {
        if (value.toLowerCase() === "true") return "Yes";
        if (value.toLowerCase() === "false") return "No";
      }
      if (key === "result_type") {
        if (value == "cgpa") return "CGPA";
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      if (key === "mobile_no") {
        const prefix = this.form.applicant.mobile_prefix || "";
        return `${prefix} ${value}`.trim();
      }
      if (key === "whatsapp_mobile_no") {
        const prefix = this.form.applicant.whatsapp_mobile_prefix || "";
        return `${prefix} ${value}`.trim();
      }

      if (key.endsWith("_id")) {
        let listName = key.replace("_id", "s");
        return this.getItemName(listName, value);
      }

      return value;
    },
    getItemName(listName, id) {
      if (!this[listName]) return id;
      let item = this[listName].find((item) => item.id === id);
      return item ? item.name : id;
    },
    isObject(value, key) {
      if (
        [
          "aadhar",
          "pan",
          "ug",
          "pg",
          "sslc",
          "photo",
          "hsc",
          "deb_map",
          "employment_letter",
          "id_card",
          "kovida_certificate",
          "program_addons",
        ].includes(key)
      )
        return false;

      return value && typeof value === "object" && !(value instanceof File);
    },
    getAddressSections() {
      const sections = [];
      if (this.form.address.is_permanent_address_same === "true") {
        sections.push({
          label: "Communication Address",
          data: this.form.address.permanent_address,
          key: "permanent",
        });
      } else {
        sections.push({
          label: "Communication Address",
          data: this.form.address.current_address,
          key: "current",
        });
        sections.push({
          label: "Permanent Address",
          data: this.form.address.permanent_address,
          key: "permanent",
        });
      }
      return sections;
    },
    async handleAddonsSubmit(selectedOption) {
      this.currentSelectedAddon = selectedOption;
      this.currentSelectedOption = selectedOption.paymentOption;

      this.openProgramAddonsDialog = false;

      if (selectedOption.paymentOption === "later") {
        this.selectedInvoice = await this.createInvoice(selectedOption);
        const invoiceId = this.selectedInvoice.id;

        const payload = {
          student_program_addons: {
            application_id: this.form.applicant.application_id,
            usn_id: this.form.applicant.usn_id,
            amount: selectedOption.totalAmount,
            program_addons: selectedOption.ids,
            is_amount_paid: false,
            convinced_by_partner: this.form.applicant.partner_id,
            status: "Pending",
            invoice_id: invoiceId,
          },
        };

        await this.createStudentAddonProgramme(payload);
        this.handlePayLater();
      } else {
        this.selectedInvoice = await this.createInvoice(selectedOption);
        const invoiceId = this.selectedInvoice.id;

        const payload = {
          student_program_addons: {
            application_id: this.form.applicant.application_id,
            usn_id: this.form.applicant.usn_id,
            amount: selectedOption.totalAmount,
            program_addons: selectedOption.ids,
            is_amount_paid: false,
            convinced_by_partner: this.form.applicant.partner_id,
            status: "Pending",
            invoice_id: invoiceId,
          },
        };
        await this.createStudentAddonProgramme(payload);
        await this.processPayment(selectedOption);
      }
    },

    async getApplicantAddonProgramme() {
      try {
        const response = await this.$axios.get(
          `/get_student_program_addons_by_application?application_id=${this.form.applicant.application_id}`,
        );
        if (response.data.data) {
          this.applicant_addon_programmes = response.data.data[0];
        }
      } catch (error) {
        console.error("Error creating student addon programme:", error);
      }
    },

    async createStudentAddonProgramme(payload) {
      try {
        const response = await this.$axios.post(
          "/create_student_program_addon",
          payload,
        );
        if (response.data.data) {
          return response.data.data;
        }
      } catch (error) {
        console.error("Error creating student addon programme:", error);
      }
    },

    async createInvoice(selectedOption) {
      try {
        const invoiceData = {
          application_id: this.form.applicant.application_id,
          usn_id: this.form.applicant.usn_id,
          type: "Addon-Programme",
          amount: selectedOption.totalAmount,
          from_date: new Date().toISOString().split("T")[0],
          to_date: this.getMaxEnrollmentDeadline(selectedOption.details),
          program_add_ons: selectedOption.ids,
          is_combined_with_tuition_fee: false,
          status: "Pending",
        };

        const response = await this.$axios.post(
          "/create_student_addon_invoices",
          { addon_invoices: invoiceData },
        );

        if (response.data.data) {
          return response.data.data;
        }
      } catch (error) {
        console.error("Error creating invoice:", error);
        this.$store.commit("showSnackbar", {
          message: error.response?.data?.message || "Error creating invoice",
          color: "error",
        });
        throw error;
      }
    },

    getFixedAmount(type) {
      const fee = parseFloat(this.form.applicant.program_addon_amount);
      if (isNaN(fee)) return "0.00";
      if (type == "Direct") return fee.toFixed(2);
      if (type == "Split") return fee.toFixed(2);
    },
    getPaymentPayload(selectedOption) {
      return {
        mercid: this.env.merchantid,
        amount: this.getFixedAmount("Direct"),
        return_url: this.env.program_addon_ru,
        additional_info: {
          additional_info1: this.form.applicant.application_id,
          additional_info2: this.form.applicant.email,
          additional_info3: "Add-on Programme",
          additional_info4: String(
            this.getMaxEnrollmentDeadline(selectedOption.details),
          ),
          additional_info5: String(this.form.applicant.program_addons),
          additional_info6: this.selectedInvoice.id || null,
          additional_info7: "application_form",
        },
        split_payment: this.env.getSplitPayment(
          this.form.applicant.program_addon_amount,
        ),
      };
    },
    async processPayment(selectedOption, payload) {
      this.$store.commit("showSnackbar", {
        message: "Redirecting to payment gateway...",
        color: "info",
      });
      this.loading = true;
      await this.$axios
        .post(this.env.billdesk_url, this.getPaymentPayload(selectedOption))
        .then((res) => {
          if (res.status == 200) {
            const { rdata, bdorderid, order_id, ecom_order_id, order_ref_no } =
              res.data;
            const form = document.createElement("form");
            form.method = "POST";
            form.action = this.env.billdesk_sdk_url;
            form.style.display = "none";
            const inputs = {
              bdorderid,
              merchantid: this.env.merchantid,
              rdata,
            };
            Object.entries(inputs).forEach(([name, value]) => {
              const input = document.createElement("input");
              input.type = "hidden";
              input.name = name;
              input.value = value;
              form.appendChild(input);
            });
            document.body.appendChild(form);
            this.createBilldeskHistory(
              order_id,
              ecom_order_id,
              order_ref_no,
            ).then(() => {
              this.loading = false;
              form.submit();
            });
          } else {
            this.$store.commit("showSnackbar", {
              message: res.data.message,
              color: "error",
            });
          }
        })
        .catch(() => {
          this.loading = false;
          this.$store.commit("showSnackbar", {
            message: "Error in payment, Please try again",
            color: "error",
          });
        });
    },

    async createBilldeskHistory(order_id, ecom_order_id, order_ref_no) {
      const data = {
        order_id: order_id,
        ecom_order_id: ecom_order_id,
        order_ref_no: order_ref_no,
        name: this.form.applicant.name,
        email: this.form.applicant.email,
        mobile_no: this.form.applicant.mobile_no,
        program_id: String(this.form.applicant.program_id),
        amount: String(this.form.applicant.program_addon_amount),
        current_status: "initiated",
        fee_type: "Add-on Programme",
      };
      await this.$axios
        .post(`/billdesk_track_history`, { billdesk_track_history: data })
        .then((res) => {
          console.log("saved history");
        });
    },

    getMaxEnrollmentDeadline(selectedAddons) {
      if (!selectedAddons || selectedAddons.length === 0) {
        return new Date().toISOString().split("T")[0];
      }

      const deadlines = selectedAddons.map(
        (a) => new Date(a.feeSchedule.enrollment_end),
      );
      return new Date(Math.max(...deadlines)).toISOString().split("T")[0];
    },

    async handleSkipAddons(selected) {
      this.currentSelectedOption = selected?.paymentOption;

      try {
        // 1. Fetch & delete addon invoice
        const invoiceRes = await this.$axios.get(
          `/get_addon_invoice_by_application_id?application_id=${this.form.applicant.application_id}`,
        );
        const invoiceId = invoiceRes.data?.data?.id;
        if (invoiceId) {
          await this.$axios.delete(
            `/delete_applicant_by_addon_invoice?application_id=${this.form.applicant.application_id}`,
          );
        }
      } catch (error) {
        // No invoice found — safe to continue
        console.warn("No addon invoice to delete:", error);
      }

      try {
        // 2. Delete student_program_addon record
        const addonRes = await this.$axios.get(
          `/get_student_program_addons_by_application?application_id=${this.form.applicant.application_id}`,
        );
        const hasRecord = addonRes.data?.data?.length > 0;
        if (hasRecord) {
          await this.$axios.delete(
            `/delete_applicant_by_application_id?application_id=${this.form.applicant.application_id}`,
          );
        }
      } catch (error) {
        console.warn("No student addon record to delete:", error);
      }

      // 3. Proceed with confirmation flow
      if (this.payment_order_status?.auth_status === "0399") {
        this.failureDialogMessage =
          "Your payment was unsuccessful. Are you sure you want to skip programme add-ons and submit your application?";
        this.showFailureDialog = true;
      } else {
        this.confirmationText =
          "Are you sure you want to skip programme add-ons and submit your application?";
        this.confirmationDialog = true;
      }
    },
    handlePayLater() {
      this.currentSelectedOption = "later";
      if (this.payment_order_status.auth_status === "0399") {
        this.failureDialogMessage =
          "Your payment was unsuccessful. Are you sure you want to pay later for programme add-ons and submit your application?";
        this.showFailureDialog = true;
      } else {
        this.confirmationText =
          "Are you sure you want to save your selected programme add-ons and submit your application?";
        this.confirmationDialog = true;
      }
    },
    async fetchFeeSchedules() {
      try {
        const params = {};
        if (this.form?.applicant?.program_id)
          params.program_id = this.form.applicant?.program_id;
        if (this.form?.applicant?.partner_id)
          params.partner_id = this.form.applicant?.partner_id;
        if (this.form.applicant?.batch)
          params.batch = this.form.applicant?.batch;
        if (this.form.applicant?.year) params.year = this.form.applicant?.year;

        const response = await this.$axios.get("/get_program_addons", {
          params,
        });
        this.addonfeeSchedules = response.data.data || [];
      } catch (error) {
        console.error("Error fetching fee schedules:", error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Card Title */
.v-card-title {
  font-weight: bold;
}

/* Section Title */
.section-title {
  font-weight: bold;
  font-size: 14px;
  margin-bottom: 2px;
}

/* Field Container */
.field-container {
  padding: 5px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Labels and Values */
.field-label {
  font-weight: bold;
  color: #424242;
  font-size: 13px;
}

.field-value {
  color: #616161;
  font-size: 13px;
}
/* .field-label {
  width: 200px;
  font-weight: bold;
  text-align: right;
  padding-right: 10px;
  display: flex;
  align-items: center;
}
.field-value {
  flex: 1;
  display: flex;
  align-items: center;
} */
.headline {
  font-size: 20px;
  font-weight: bold;
}
.subtitle {
  font-size: 15px;
  font-weight: bold;
}

@media print {
  .no-print {
    display: none;
  }
}
.custom-grid {
  display: grid;
  grid-template-columns: 1fr; /* Default: Mobile (1 column) */
  gap: 6px;
}

/* Tablet ≥600px: 2 columns */
@media (min-width: 600px) {
  .custom-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Medium Desktop ≥960px: 3 columns */
@media (min-width: 960px) {
  .custom-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Large Desktop ≥1280px: 5 columns */
@media (min-width: 1280px) {
  .custom-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

/* === Grid Item Layouts === */

/* Profile photo */
.div1 {
  grid-column: span 1;
  grid-row: span 2;
  display: flex;
  justify-content: center;
}

/* These items will flow naturally in grid — no fixed columns needed */
.div3,
.div4,
.div5,
.div6 {
  grid-column: span 1;
}

/* Section titles take full row */
.section-title {
  grid-column: 1 / -1;
  font-weight: 600;
  margin-top: 2px;
  font-size: 14px;
}

/* Default field item */
.field-item {
  grid-column: span 1;
}
</style>
